import React from "react";
import { X, FileText, Download, Globe } from "lucide-react";

// Scalable template configuration - easy to add new countries
const TEMPLATE_CONFIG = {
  "South Korea": {
    templates: [
      {
        category: "Tourist Visa",
        files: [
          { label: "English Form", path: "/templates/english_tourist.pdf", language: "EN" },
          { label: "Korean Form", path: "/templates/korea/korean_tourist.pdf", language: "KO" },
        ],
      },
      {
        category: "Business Visa",
        files: [
          { label: "English Form", path: "/templates/english_business.pdf", language: "EN" },
          { label: "Korean Form", path: "/templates/korea/korean_business.pdf", language: "KO" },
        ],
      },
    ],
  },

  Thailand: {
    templates: [
      {
        category: "Tourist Visa",
        files: [
          { label: "English Form", path: "/templates/english_tourist.pdf", language: "EN" },
          { label: "Thai Form", path: "/templates/thailand/thai_tourist.pdf", language: "TH" },
        ],
      },
      {
        category: "Business Visa",
        files: [
          { label: "English Form", path: "/templates/english_business.pdf", language: "EN" },
          { label: "Thai Form ", path: "/templates/thailand/thai_business.pdf", language: "TH" },
        ],
      },
    ],
  },
  India: {
    templates: [
      {
        category: "Tourist Visa",
        files: [
          { label: "English Form", path: "/templates/english_tourist.pdf", language: "EN" },
          
        ],
      },
      {
        category: "Business Visa",
        files: [
          { label: "English Form", path: "/templates/english_business.pdf", language: "EN" },
        ],
      },
    ],
  },
};

export default function DataTemplateModal({ open, onClose, country }) {
  if (!open) return null;

  const countryConfig = TEMPLATE_CONFIG[country];
  const hasTemplates = !!countryConfig;

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={handleBackdropClick}
    >
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">
                  Download Visa Templates
                </h3>
                {hasTemplates && (
                  <p className="text-blue-100 text-sm mt-0.5 flex items-center gap-1.5">
                    {country}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/20 text-white transition-colors"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {!hasTemplates ? (
            // No country selected or unsupported country
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-amber-600" />
              </div>
              <h4 className="text-lg font-semibold text-slate-900 mb-2">
                Country Not Set
              </h4>
              <p className="text-sm text-slate-600 max-w-md mx-auto">
                Please set your country in your profile to access the appropriate
                visa templates. Currently supported:{" "}
                <span className="font-medium">
                  South Korea, Thailand, India
                </span>
              </p>
            </div>
          ) : (
            // Display templates
            <div className="space-y-6">
              {countryConfig.templates.map((section, index) => (
                <div key={index}>
                  <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <div className="w-1 h-4 bg-blue-600 rounded-full"></div>
                    {section.category}
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {section.files.map((file, fileIndex) => (
                      <TemplateCard
                        key={fileIndex}
                        label={file.label}
                        language={file.language}
                        path={file.path}
                      />
                    ))}
                  </div>
                </div>
              ))}

            
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-slate-50 border-t border-slate-200 px-6 py-4">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-5 py-2.5 bg-white hover:bg-slate-100 text-slate-700 text-sm font-medium rounded-lg border border-slate-300 shadow-sm transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Reusable Template Card Component
function TemplateCard({ label, language, path }) {
  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = path;
    link.download = path.split("/").pop();
    link.click();
  };

  return (
    <div className="group relative bg-white border border-slate-200 rounded-lg p-4 hover:border-blue-400 hover:shadow-md transition-all duration-200">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-slate-900">{label}</span>
            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded">
              {language}
            </span>
          </div>
          <p className="text-xs text-slate-500">PDF Document</p>
        </div>
        <button
          onClick={handleDownload}
          className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-colors group"
          title={`Download ${label}`}
        >
          <Download className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}